import React from 'react';
import { styled } from '@compiled/react';
import FocusRing from '@atlaskit/focus-ring';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { MINIMISED_DROPZONE_CONTAINER_ID } from '@atlassian/jira-issue-create-common-types/src/common/constants/index.tsx';
import { JiraBottomRightCornerOutlet } from '@atlassian/jira-layout-controller/src/ui/bottom-right-corner/outlet/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireScreenAnalyticsDeferred,
	MountEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useEnterFocusState } from '../../../controllers/enter-focus-state/index.tsx';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import type { MinimizedContainerProps } from '../../../types.tsx';
import messages from './messages.tsx';

export const Minimized = ({ children, className }: MinimizedContainerProps) => {
	const { unminimizeModal, previousView, isTransitionInState, setShouldFocusChanger } =
		useMinimizableModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { minimizedButtonRef, onKeyDown, wrapperRef } = useEnterFocusState();

	const isViewEntered = isTransitionInState(['minimized'], TransitionState.ENTERED);

	const unminimize = () => {
		if (!isViewEntered) return;
		setShouldFocusChanger(true);
		unminimizeModal('modal');
		const analyticsEvent = createAnalyticsEvent({
			action: 'container clicked',
			actionSubject: 'minimized',
		});
		fireUIAnalytics(analyticsEvent, {
			source: 'globalIssueCreateModal',
			previousGICViewState: previousView,
		});
	};

	return (
		<JiraBottomRightCornerOutlet orderFromRight={3} id="issue-create-minimised-modal">
			<MinimizedOuterWrapper
				id={MINIMISED_DROPZONE_CONTAINER_ID}
				tabIndex={-1}
				ref={wrapperRef}
				data-testid={
					// testid should only be available when view is not transitioning
					// otherwise CY tries to click the view before its last position
					isViewEntered ? 'minimizable-modal.ui.modal-container.minimized-outer-wrapper' : undefined
				}
			>
				<MountEvent onMount={fireScreenAnalyticsDeferred} />
				<FocusRing isInset>
					<MinimizedWrapper
						ref={minimizedButtonRef}
						data-testid={
							isViewEntered ? 'minimizable-modal.ui.modal-container.minimized' : undefined
						}
						onKeyDown={onKeyDown}
						onClick={unminimize}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
						aria-label={formatMessage(messages.ariaLabel)}
					>
						<MinimizedText>{children}</MinimizedText>
					</MinimizedWrapper>
				</FocusRing>
			</MinimizedOuterWrapper>
		</JiraBottomRightCornerOutlet>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedWrapper = styled.button({
	border: 0,
	boxSizing: 'border-box',

	backgroundColor: token('elevation.surface.raised'),
	height: '48px',
	width: '300px',
	borderRadius: '8px',
	display: 'flex',
	alignItems: 'center',
	paddingTop: 0,
	paddingRight: token('space.200'),
	paddingBottom: 0,
	paddingLeft: token('space.200'),
	cursor: 'pointer',
	transition: 'background 0.1s ease-out',
	boxShadow: token('elevation.shadow.overlay'),

	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		outline: 'none',
	},

	'&:active': {
		backgroundColor: token('elevation.surface.hovered'),
		outline: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:focus:not(:focus-visible)': {
		boxShadow: 'none',
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedText = styled.div({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',

	color: token('color.text'),
	font: token('font.heading.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MinimizedOuterWrapper = styled.div({
	'&:focus': {
		outline: 'none',
	},
});
